import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Profile2User } from 'iconsax-react';
import TableWrapper from './TableWrapper';
import ProductsFilter from './ProductsFilter';
import SellerProductsTable, { SellerProductRow } from './SellerProductsTable';
import TitleText from '../../../atoms/TitleText';
import InfoText from '../../../atoms/InfoText';
import EmptyImg from '../../../../assets/images/empty.png';
import EmptyDarkImg from '../../../../assets/images/empty-dark.png';
import { useGetProductsStatsQuery, useGetSellerProductsByAdminQuery } from '../../../../store/services/products';
import { usePublicState } from '../../../../store/slices/publicSlice';


interface SellerProductsContentProps {
  sellerId?: string;
  sellerName?: string;
}

export default function SellerProductsContent({ sellerId, sellerName }: SellerProductsContentProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    stockStatus: string[];
    publishStatus: string[];
    fromDate?: string;
    toDate?: string;
  }>({
    stockStatus: [],
    publishStatus: [],
    fromDate: undefined,
    toDate: undefined,
  });

  const { search } = usePublicState();
  const sellerProductsSearch = search['sellerProductsSearch'] || '';

  const { data: statsData, isLoading: statsLoading } = useGetProductsStatsQuery({ page: 0, search: '' });

  const { data: sellerProductsData, isLoading: sellerProductsLoading, refetch: refetchSellerProducts } = useGetSellerProductsByAdminQuery(
    {
      sellerId: sellerId || '',
      stockStatuses: filters.stockStatus.length > 0 ? filters.stockStatus.join(',') : undefined,
      isPublish: filters.publishStatus.length === 1 ? (filters.publishStatus[0] === 'true') : undefined,
      search: sellerProductsSearch || undefined,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    },
    { skip: !sellerId }
  );

  React.useEffect(() => {
    if (sellerProductsData) {
      console.log('Seller products response:', sellerProductsData);
    }
  }, [sellerProductsData]);

  const stats = statsData?.payload || {
    totalRequests: 12,
    approvedRequests: 10,
    rejectedRequests: 1,
    editedRequest: 1,
  };

  const statCardsConfig = [
    {
      key: 'total',
      title: t('sellers.products.totalRequests'),
      value: stats?.totalRequests ?? 0,
      percentage: '+8%',
      trendText: t('sellers.products.thanLastWeek'),
      icon: <Profile2User size={28} variant="Outline" />,
    },
    {
      key: 'approved',
      title: t('sellers.products.approvedRequests'),
      value: stats?.approvedRequests ?? 0,
      percentage: '+8%',
      trendText: t('sellers.products.thanLastWeek'),
      icon: <Profile2User size={28} variant="Outline" />,
    },
    {
      key: 'rejected',
      title: t('sellers.products.rejected'),
      value: stats?.rejectedRequests ?? 0,
      percentage: '+8%',
      trendText: t('sellers.products.thanLastWeek'),
      icon: <Profile2User size={28} variant="Outline" />,
    },
    {
      key: 'edited',
      title: t('sellers.products.edited'),
      value: stats?.editedRequest ?? 0,
      percentage: '+8%',
      trendText: t('sellers.products.thanLastWeek'),
      icon: <Profile2User size={28} variant="Outline" />,
    },
  ];

  const rawProducts = sellerProductsData?.payload?.content || [];

  const filteredProducts = rawProducts.filter((p: any) => {
    if (filters.stockStatus.length > 0) {
      if (!p.stockStatus || !filters.stockStatus.includes(p.stockStatus.code)) return false;
    }
    if (filters.publishStatus.length > 0) {
      const isPub = p.publishStatus?.code === 'PUBLISHED' || p.isPublish || false;
      const match = filters.publishStatus.some(val => (val === 'true' && isPub) || (val === 'false' && !isPub));
      if (!match) return false;
    }
    return true;
  });

  // Map API products for display
  const products: SellerProductRow[] = filteredProducts.map((p: any) => ({
    productId: p.id || p.productId || '',
    productName: p.name || p.productName || '',
    imageUrl: p.imageUrl || p.photoUrl || null,
    createdBy: p.createdBy || p.sellerName || '',
    availableQnt: p.availableQnt ?? p.quantity ?? 0,
    dateCreated: p.createdDate || p.dateCreated || '',
    status: p.status || { code: 'APPROVED', name: 'Approved' },
    publishStatus: p.publishStatus || null,
    isPublish: p.isPublish ?? false,
    serial: p.serial || '',
    stockStatus: p.stockStatus || null,
    actions: {
      canPreviewProduct: p.actions?.canPreviewProduct ?? true,
      canEditProduct: p.actions?.canEditProduct ?? true,
      canDuplicateProduct: p.actions?.canDuplicateProduct ?? true,
      canDeleteProduct: p.actions?.canDeleteProduct ?? true,
      canPublishProduct: p.actions?.canPublishProduct ?? true,
    },
  }));

  const totalProductsCount = rawProducts.length;
  const hasProducts = products.length > 0;
  
  const hasActiveFilters = Boolean(
    sellerProductsSearch ||
    filters.stockStatus.length > 0 ||
    filters.publishStatus.length > 0 ||
    filters.fromDate ||
    filters.toDate
  );

  return (
    <div className="flex flex-col gap-8" data-testid="store-products-tab">
      {/* Products Table or Empty State */}
      {totalProductsCount > 0 || sellerProductsLoading || hasActiveFilters ? (
        <TableWrapper
          title={t('sellers.products.allProducts')}
          subTitle={t('sellers.products.displayAll')}
          hasSearch
          searchName="sellerProductsSearch"
          searchPlaceholder={t('sellers.products.searchHere')}
          inlineSearchOnMobile={true}
          actions={
            <div className="flex gap-2" data-testid="store-products-header-actions">
              <ProductsFilter sellerName={sellerName} onFilterChange={setFilters} />
            </div>
          }
          dataTestId="store-products-panel"
        >
          <div data-testid="store-products-panel-table-slot">
            {hasProducts || sellerProductsLoading ? (
              <SellerProductsTable
                sellerId={sellerId}
                products={products}
                loading={sellerProductsLoading}
                onRefresh={refetchSellerProducts}
              />
            ) : (
              <div
                className="flex flex-col items-center justify-center py-12 px-4 text-center gap-4 bg-white dark:bg-dark-surface"
                data-testid="store-products-search-empty-state"
              >
                <div className="relative w-36 h-36">
                  <img
                    src={EmptyImg}
                    alt="Empty search"
                    className="w-full h-full object-contain block dark:hidden"
                  />
                  <img
                    src={EmptyDarkImg}
                    alt="Empty search"
                    className="w-full h-full object-contain hidden dark:block"
                  />
                </div>
                <div className="flex flex-col gap-2 max-w-lg">
                  <TitleText
                    text={t('sellers.products.noResultsFound')}
                    size="xl"
                    className="!font-bold dark:text-dark-text-primary"
                  />
                  <InfoText
                    text={t('sellers.products.noResultsFoundDesc')}
                    size="sm"
                    className="text-gray-500 dark:text-dark-text-secondary"
                  />
                </div>
              </div>
            )}
          </div>
        </TableWrapper>
      ) : (
        <div
          className="flex flex-col items-center justify-center py-16 px-4 text-center gap-6 bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border"
          data-testid="store-products-empty-state"
        >
          <div
            className="relative w-48 h-48"
            data-testid="store-products-empty-image"
          >
            <img
              src={EmptyImg}
              alt="Empty products"
              className="w-full h-full object-contain block dark:hidden"
            />
            <img
              src={EmptyDarkImg}
              alt="Empty products"
              className="w-full h-full object-contain hidden dark:block"
            />
          </div>

          <div className="flex flex-col gap-2 max-w-lg">
            <TitleText
              text={t('sellers.products.nothingHereYet')}
              size="2xl"
              className="!font-bold dark:text-dark-text-primary"
              data-testid="store-products-empty-title"
            />
            <InfoText
              text={t('sellers.products.nothingHereYetDesc')}
              size="base"
              className="text-gray-500 dark:text-dark-text-secondary"
              data-testid="store-products-empty-desc"
            />
          </div>
        </div>
      )}
    </div>
  );
}
